import { useEffect, useId, useRef, useState } from "react";
import { FormInput } from "../../Inputs/FormInput";
import { INVALID_STRING_PATTERN } from "@/lib/utils/default-returner";

type TableCellContentStringProps = {
  isFilter?: boolean;
  border?: string;
  editable: boolean;
  value?: string;
  isSelected: boolean;
  isEditing: boolean;
  onChangeValue: (value: string) => void;
  onFocusAction?: () => void;
  onBlurAction?: () => void; // Optional blur handler
  error?: string | null;
  height?: string;
  surface?: string;
  fontSize?: string;
  padding?: string;
  rounded?: string;
  label?: string;
  margin?: string;
  className?: string;
  maxLength?: number;
  copy?: boolean;
  viewOnly?: boolean;
  /**
   * PB-TN-3: optional explicit id. Supply one when a test or an external
   * `htmlFor` needs to target this specific input; otherwise a unique id is
   * generated per instance.
   */
  id?: string;
};

export const TableCellContentString: React.FC<TableCellContentStringProps> = ({
  id: idProp,
  isFilter,
  border = "outline-tertiary border focus-within:outline-focus",
  editable,
  value,
  isSelected,
  isEditing,
  onFocusAction,
  onBlurAction,
  onChangeValue,
  error,
  height = "h-9",
  surface = "",
  fontSize = "body-2-regular",
  padding = "",
  rounded = "rounded-lg",
  label,
  margin = "",
  className = "",
  maxLength,
  copy = false,
  viewOnly,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  // PB-N3-3 — every instance needs its OWN id.
  //
  // This was `id={`edit-${value}`}`, derived from the VALUE prop. Typed text
  // lives in internal state and the ref-driven create wizard never re-renders
  // its parents, so every TCCString that mounted empty kept `id="edit-"`
  // FOREVER. Step 0's name and description, and step 1's venue/address/Online
  // link, all collided on that one id — and since FormInput renders
  // `htmlFor={id}`, every label resolved to the FIRST `id="edit-"` node, the
  // Event Name input.
  //
  // The user-visible damage was real, not just a11y hygiene: clicking the
  // "Description" label focused the NAME field, and `getByLabel("Description")
  // .fill(...)` overwrote the event name while the description stayed empty.
  //
  // `useId` is stable across re-renders and unique per instance, which is
  // exactly the contract the old expression was reaching for and failing at.
  const generatedId = useId();
  const inputId = idProp ?? `edit-${generatedId}`;
  const [string, setStringState] = useState(value ?? "");

  useEffect(() => {
    setStringState(value ?? "");
  }, [value]);

  function setString(string: string) {
    if (!INVALID_STRING_PATTERN.test(string)) {
      setStringState(string);
      onChangeValue(string);
    }
  }
  const handleChange = () => {
    // Check if the new value contains invalid characters
    if (!INVALID_STRING_PATTERN.test(string)) onChangeValue(string);
    if (onBlurAction) onBlurAction();
  };

  return (
    <>
      {isEditing && isSelected && (editable || isFilter) ? (
        <div className={`flex flex-col flex-1 ${className}`}>
          <FormInput
            copy={copy}
            maxLength={maxLength}
            ref={inputRef}
            iconPaddingLeft=""
            iconPaddingRight=""
            disabled={viewOnly}
            id={inputId}
            label={label}
            border={border}
            rounded={rounded}
            margin={margin}
            height={height}
            fontSize={fontSize}
            surface={surface}
            paddingInput={padding}
            value={string}
            onChangeAction={setString}
            onEnterAction={() => inputRef?.current?.blur()}
            onBlurAction={handleChange}
            width="w-full"
            onFocusAction={onFocusAction}
          />
          {error && (
            <p role="alert" className="mt-1 body-3-regular font-error">
              {error}
            </p>
          )}
        </div>
      ) : (
        <p>{value}</p>
      )}
    </>
  );
};
